import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useModuleState from './useModuleState';

export interface TimerModuleState {
  mode: 'countdown' | 'countup';
  durationSec: number;
  elapsedBeforeStartSec: number;
  startedAt: number | null;
  isRunning: boolean;
  lastCompletedAt: number | null;
}

interface UseTimerModuleOptions {
  storageKey?: string;
  mode: 'countdown' | 'countup';
  defaultDurationSec?: number;
}

function createDefaultState(mode: 'countdown' | 'countup', durationSec: number): TimerModuleState {
  return {
    mode,
    durationSec,
    elapsedBeforeStartSec: 0,
    startedAt: null,
    isRunning: false,
    lastCompletedAt: null,
  };
}

function playCompletionBeep() {
  if (typeof window === 'undefined') return;

  const AudioContextClass = window.AudioContext || (window as typeof window & {
    webkitAudioContext?: typeof AudioContext;
  }).webkitAudioContext;

  if (!AudioContextClass) return;

  const audioContext = new AudioContextClass();
  const oscillator = audioContext.createOscillator();
  const gainNode = audioContext.createGain();

  oscillator.type = 'sine';
  oscillator.frequency.setValueAtTime(880, audioContext.currentTime);
  oscillator.frequency.exponentialRampToValueAtTime(660, audioContext.currentTime + 0.18);

  gainNode.gain.setValueAtTime(0.0001, audioContext.currentTime);
  gainNode.gain.exponentialRampToValueAtTime(0.16, audioContext.currentTime + 0.02);
  gainNode.gain.exponentialRampToValueAtTime(0.0001, audioContext.currentTime + 0.32);

  oscillator.connect(gainNode);
  gainNode.connect(audioContext.destination);

  oscillator.start();
  oscillator.stop(audioContext.currentTime + 0.34);

  oscillator.onended = () => {
    void audioContext.close();
  };
}

const useTimerModule = (
  moduleId: string,
  { storageKey, mode, defaultDurationSec = 25 * 60 }: UseTimerModuleOptions
) => {
  const { state, saveState } = useModuleState<TimerModuleState>(
    moduleId,
    createDefaultState(mode, defaultDurationSec),
    { storageKey }
  );
  const [now, setNow] = useState(Date.now());
  const hasMountedRef = useRef(false);
  const lastCompletedRef = useRef<number | null>(null);

  useEffect(() => {
    if (!state.isRunning) return undefined;

    const intervalId = window.setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [state.isRunning]);

  const elapsedSec = useMemo(() => {
    if (!state.isRunning || !state.startedAt) {
      return state.elapsedBeforeStartSec;
    }

    return state.elapsedBeforeStartSec + Math.floor((now - state.startedAt) / 1000);
  }, [now, state.elapsedBeforeStartSec, state.isRunning, state.startedAt]);

  const displaySec = state.mode === 'countdown'
    ? Math.max(0, state.durationSec - elapsedSec)
    : elapsedSec;

  useEffect(() => {
    if (!state.isRunning) {
      return;
    }

    const isCountdownComplete = state.mode === 'countdown' && elapsedSec >= state.durationSec;
    const isCountupComplete = state.mode === 'countup' && state.durationSec > 0 && elapsedSec >= state.durationSec;

    if (!isCountdownComplete && !isCountupComplete) {
      return;
    }

    void saveState((current) => ({
      ...current,
      elapsedBeforeStartSec: current.durationSec,
      startedAt: null,
      isRunning: false,
      lastCompletedAt: Date.now(),
    }));
  }, [elapsedSec, saveState, state.durationSec, state.isRunning, state.mode]);

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      lastCompletedRef.current = state.lastCompletedAt;
      return;
    }

    if (!state.lastCompletedAt || lastCompletedRef.current === state.lastCompletedAt) {
      return;
    }

    lastCompletedRef.current = state.lastCompletedAt;
    playCompletionBeep();
  }, [state.lastCompletedAt]);

  const start = useCallback(async () => {
    if (state.isRunning) return;
    await saveState((current) => ({
      ...current,
      startedAt: Date.now(),
      isRunning: true,
    }));
  }, [saveState, state.isRunning]);

  const pause = useCallback(async () => {
    if (!state.isRunning) return;
    await saveState((current) => {
      const startedAt = current.startedAt ?? Date.now();
      const elapsed = current.elapsedBeforeStartSec + Math.floor((Date.now() - startedAt) / 1000);
      return {
        ...current,
        elapsedBeforeStartSec: elapsed,
        startedAt: null,
        isRunning: false,
      };
    });
  }, [saveState, state.isRunning]);

  const reset = useCallback(async () => {
    await saveState((current) => ({
      ...current,
      elapsedBeforeStartSec: 0,
      startedAt: null,
      isRunning: false,
      lastCompletedAt: current.lastCompletedAt,
    }));
  }, [saveState]);

  const setDurationSec = useCallback(async (durationSec: number) => {
    await saveState((current) => ({
      ...current,
      durationSec,
      elapsedBeforeStartSec: 0,
      startedAt: null,
      isRunning: false,
    }));
  }, [saveState]);

  return {
    state,
    elapsedSec,
    displaySec,
    start,
    pause,
    reset,
    setDurationSec,
  };
};

export default useTimerModule;
